import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChatCircleDots, PaperPlaneRight, Users, Trash, Prohibit } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_COLORS = {
  sender:   { bg: "rgba(45,74,62,0.10)", color: "var(--primary)",   label: "Remetente" },
  receiver: { bg: "rgba(37,99,235,0.10)", color: "#2563eb",         label: "Destinatário" },
  admin:    { bg: "rgba(217,108,74,0.10)", color: "var(--accent)",  label: "BluMove" },
  guest:    { bg: "rgba(120,120,120,0.10)", color: "var(--text-muted)", label: "Convidado" },
};

/**
 * ShipmentChat — real-time per-shipment chat (Item 5).
 * Used in `MyShipments` (authenticated) and `PublicTracking` (guest with name).
 * Hybrid auth: JWT cookie if logged in, else falls back to a guest name input.
 */
export default function ShipmentChat({ trackingCode, defaultName = "" }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [name, setName] = useState(defaultName);
  const [me, setMe] = useState(null); // {role, name}
  const [members, setMembers] = useState(0);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [typingNames, setTypingNames] = useState([]);  // names currently typing
  const [readers, setReaders] = useState({});  // {name: lastReadIso}
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [unseenCount, setUnseenCount] = useState(0);
  const wsRef = useRef(null);
  const scrollerRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const typingExpiryRef = useRef({});

  const wsUrl = useCallback((authorName) => {
    // BACKEND_URL is https://...; convert to wss
    const base = (BACKEND_URL || window.location.origin).replace(/^http/, "ws");
    const qs = authorName ? `?author_name=${encodeURIComponent(authorName)}` : "";
    return `${base}/api/ws/chat/${trackingCode}${qs}`;
  }, [trackingCode]);

  const connect = useCallback((authorName) => {
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (e) { /* ignore */ }
    }
    let ws;
    try {
      ws = new WebSocket(wsUrl(authorName));
    } catch (e) {
      setError(t("chat.connect_error"));
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setError(t("chat.connect_error"));
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "hello") {
          setMe(data.you);
          setMembers(data.members || 1);
          setMessages(data.history || []);
        } else if (data.type === "presence") {
          setMembers(data.members || 0);
        } else if (data.type === "message") {
          setMessages((prev) => [...prev, data]);
        } else if (data.type === "message_deleted") {
          setMessages((prev) => prev.filter((m) => m.id !== data.id));
        } else if (data.type === "blocked") {
          setError(t("chat.blocked"));
        } else if (data.type === "typing") {
          const k = data.author_name;
          // we cannot read latest `me` from this closure, so allow even own
          // names; render filters them out below.
          if (data.is_typing) {
            setTypingNames((prev) => prev.includes(k) ? prev : [...prev, k]);
            if (typingExpiryRef.current[k]) clearTimeout(typingExpiryRef.current[k]);
            typingExpiryRef.current[k] = setTimeout(() => {
              setTypingNames((prev) => prev.filter((n) => n !== k));
              delete typingExpiryRef.current[k];
            }, 4000);
          } else {
            setTypingNames((prev) => prev.filter((n) => n !== k));
            if (typingExpiryRef.current[k]) {
              clearTimeout(typingExpiryRef.current[k]);
              delete typingExpiryRef.current[k];
            }
          }
        } else if (data.type === "read") {
          if (data.by?.name) {
            setReaders((prev) => ({ ...prev, [data.by.name]: data.at }));
          }
        }
      } catch (e) { /* ignore */ }
    };
  }, [wsUrl, t]);

  // Try to fetch logged-in name from /api/auth/me; fall back to guest mode.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("blumove_token");
        if (token) {
          const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          if (!cancelled) {
            setName(res.data.name || defaultName);
            setIsAdmin(["admin", "super_admin"].includes(res.data.role));
            connect(res.data.name);
            return;
          }
        }
      } catch (e) { /* not logged in */ }
      if (defaultName) connect(defaultName);
    })();
    return () => {
      cancelled = true;
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) { /* ignore */ }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingCode]);

  // Auto-scroll only if user was already at the bottom; otherwise increment unseen
  useEffect(() => {
    if (!scrollerRef.current) return;
    if (isAtBottom) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      setUnseenCount(0);
    } else {
      // detect that the latest message is not from us
      const last = messages[messages.length - 1];
      if (last && (!me || last.author_name !== me.name)) {
        setUnseenCount((c) => c + 1);
      }
    }
  }, [messages, isAtBottom, me]);

  const onScrollerScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    const atBottom = distFromBottom < 24;
    setIsAtBottom(atBottom);
    if (atBottom) setUnseenCount(0);
  };

  const scrollToBottom = () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    setIsAtBottom(true);
    setUnseenCount(0);
  };

  const send = (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || !connected || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ body }));
    // Stop the typing indicator immediately
    try {
      wsRef.current.send(JSON.stringify({ type: "typing", is_typing: false }));
    } catch (e) { /* ignore */ }
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    setDraft("");
  };

  const handleDraftChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    if (!connected || !wsRef.current) return;
    const now = Date.now();
    // Throttle typing frames to 1.5s
    if (v && now - lastTypingSentRef.current > 1500) {
      try { wsRef.current.send(JSON.stringify({ type: "typing", is_typing: true })); } catch (e) { /* ignore */ }
      lastTypingSentRef.current = now;
    }
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      try { wsRef.current && wsRef.current.send(JSON.stringify({ type: "typing", is_typing: false })); } catch (e) { /* ignore */ }
    }, 2500);
  };

  // Emit a `read` frame whenever the chat is on screen and a new message arrives
  useEffect(() => {
    if (!connected || !wsRef.current || messages.length === 0) return;
    const last = messages[messages.length - 1];
    // Only mark as read if the last message is NOT from us
    if (me && last.author_name !== me.name) {
      try { wsRef.current.send(JSON.stringify({ type: "read" })); } catch (e) { /* ignore */ }
    }
  }, [messages, connected, me]);

  const startGuest = (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    connect(name.trim());
  };

  const adminDelete = async (msgId) => {
    const token = localStorage.getItem("blumove_token");
    try {
      await axios.delete(`${API}/chat/messages/${msgId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      // WS will broadcast message_deleted; UI updates automatically. Optimistic prune:
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
    } catch (e) {
      setError(t("chat.delete_error"));
    }
  };

  const adminBlock = async (authorName) => {
    if (!window.confirm(`${t("chat.confirm_block")} ${authorName}?`)) return;
    const token = localStorage.getItem("blumove_token");
    try {
      await axios.post(`${API}/chat/_admin/block-author`,
        { author_name: authorName, reason: "From chat moderation UI" },
        { headers: { Authorization: `Bearer ${token}` } });
      setError("");
      window.alert(`${authorName} ${t("chat.blocked_ok")}`);
    } catch (e) {
      setError(t("chat.block_error"));
    }
  };

  // ---- Render ----
  if (!me && !wsRef.current) {
    // Guest gate
    return (
      <div data-testid={`chat-${trackingCode}`} style={{ marginTop: 14, padding: 14, borderRadius: 12, border: "1px solid var(--border-color)", background: "var(--surface)" }}>
        <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
          <ChatCircleDots size={18} weight="duotone" style={{ color: "var(--primary)" }} />
          <span style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t("chat.title")}</span>
        </div>
        <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: 8 }}>{t("chat.guest_intro")}</p>
        <form onSubmit={startGuest} className="flex gap-2">
          <input
            data-testid={`chat-${trackingCode}-name-input`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("chat.your_name")}
            style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg)", fontSize: "0.85rem" }}
          />
          <button
            data-testid={`chat-${trackingCode}-join-btn`}
            type="submit"
            disabled={!name.trim()}
            style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, fontSize: "0.8rem", cursor: name.trim() ? "pointer" : "default", opacity: name.trim() ? 1 : 0.6 }}
          >
            {t("chat.join")}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid={`chat-${trackingCode}`} style={{ marginTop: 14, padding: 14, borderRadius: 12, border: "1px solid var(--border-color)", background: "var(--surface)" }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
        <div className="flex items-center gap-2">
          <ChatCircleDots size={18} weight="duotone" style={{ color: "var(--primary)" }} />
          <span style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t("chat.title")}</span>
          {me && (
            <span style={{ fontSize: "0.7rem", padding: "2px 8px", borderRadius: 9999, background: (ROLE_COLORS[me.role] || ROLE_COLORS.guest).bg, color: (ROLE_COLORS[me.role] || ROLE_COLORS.guest).color, fontWeight: 600 }}>
              {t(`chat.role.${me.role}`)} · {me.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1" style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
          <Users size={12} /> {members}
          <span data-testid={`chat-${trackingCode}-status`} style={{ width: 6, height: 6, borderRadius: "50%", background: connected ? "var(--success)" : "var(--text-muted)", marginLeft: 6 }} />
        </div>
      </div>

      <div
        ref={scrollerRef}
        onScroll={onScrollerScroll}
        data-testid={`chat-${trackingCode}-messages`}
        style={{ position: "relative", maxHeight: 240, overflowY: "auto", padding: 8, borderRadius: 8, background: "var(--bg)", display: "flex", flexDirection: "column", gap: 6 }}
      >
        {messages.length === 0 ? (
          <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", textAlign: "center", padding: 16 }}>{t("chat.empty")}</p>
        ) : messages.map((m) => {
          const isMine = me && m.author_name === me.name && m.author_role === me.role;
          const rc = ROLE_COLORS[m.author_role] || ROLE_COLORS.guest;
          return (
            <div key={m.id} data-testid={`chat-${trackingCode}-msg`} style={{ alignSelf: isMine ? "flex-end" : "flex-start", maxWidth: "82%" }}>
              <div style={{ fontSize: "0.65rem", color: rc.color, fontWeight: 600, marginBottom: 2, textAlign: isMine ? "right" : "left" }}>
                {m.author_name} · {t(`chat.role.${m.author_role}`)}
              </div>
              <div className="flex items-start gap-1" style={{ flexDirection: isMine ? "row-reverse" : "row" }}>
                <div style={{ padding: "8px 12px", borderRadius: 12, background: isMine ? "var(--primary)" : rc.bg, color: isMine ? "white" : "var(--text)", fontSize: "0.85rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {m.body}
                </div>
                {isAdmin && !isMine && (
                  <div className="flex items-center gap-1" style={{ alignSelf: "center" }}>
                    <button
                      data-testid={`chat-${trackingCode}-delete-${m.id}`}
                      onClick={() => adminDelete(m.id)}
                      title={t("chat.admin_delete")}
                      style={{ background: "none", border: "1px solid var(--border-color)", borderRadius: 6, padding: 4, cursor: "pointer", color: "#dc2626", display: "flex" }}
                    >
                      <Trash size={11} weight="bold" />
                    </button>
                    {(m.author_role === "guest" || m.author_role === "receiver" || m.author_role === "sender") && (
                      <button
                        data-testid={`chat-${trackingCode}-block-${m.id}`}
                        onClick={() => adminBlock(m.author_name)}
                        title={t("chat.admin_block")}
                        style={{ background: "none", border: "1px solid var(--border-color)", borderRadius: 6, padding: 4, cursor: "pointer", color: "var(--accent)", display: "flex" }}
                      >
                        <Prohibit size={11} weight="bold" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div style={{ fontSize: "0.6rem", color: "var(--text-muted)", marginTop: 2, textAlign: isMine ? "right" : "left", display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", alignItems: "center", gap: 6 }}>
                <span>{new Date(m.created_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}</span>
                {isMine && (() => {
                  // Find the freshest read-at among other readers, after this message
                  const seenAt = Object.entries(readers)
                    .filter(([n, at]) => at > m.created_at && n !== me.name)
                    .map(([_, at]) => at)
                    .sort()[0];
                  if (!seenAt) return null;
                  return (
                    <span data-testid={`chat-${trackingCode}-seen-${m.id}`} style={{ color: "#2563eb" }}>
                      ✓✓ {t("chat.seen_at")} {new Date(seenAt).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {!isAtBottom && unseenCount > 0 && (
        <button
          data-testid={`chat-${trackingCode}-new-msg-pill`}
          onClick={scrollToBottom}
          className="flex items-center justify-center gap-1"
          style={{
            position: "relative",
            margin: "-30px auto 0",
            zIndex: 5,
            padding: "6px 14px",
            borderRadius: 9999,
            border: "none",
            background: "var(--primary)",
            color: "white",
            fontSize: "0.72rem",
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(45,74,62,0.25)",
            display: "flex",
            alignItems: "center",
          }}
        >
          {t("chat.new_messages")} {unseenCount} ⬇
        </button>
      )}

      {typingNames.filter((n) => n !== me?.name).length > 0 && (
        <div data-testid={`chat-${trackingCode}-typing`} style={{ fontSize: "0.7rem", color: "var(--text-muted)", padding: "4px 6px", fontStyle: "italic" }}>
          {typingNames.filter((n) => n !== me?.name).join(", ")} {t("chat.is_typing")}
        </div>
      )}

      <form onSubmit={send} className="flex gap-2" style={{ marginTop: 6 }}>
        <input
          data-testid={`chat-${trackingCode}-input`}
          value={draft}
          onChange={handleDraftChange}
          placeholder={t("chat.placeholder")}
          disabled={!connected}
          style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg)", fontSize: "0.85rem" }}
        />
        <button
          data-testid={`chat-${trackingCode}-send-btn`}
          type="submit"
          disabled={!connected || !draft.trim()}
          style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: "var(--primary)", color: "white", cursor: connected && draft.trim() ? "pointer" : "default", opacity: connected && draft.trim() ? 1 : 0.5, display: "flex", alignItems: "center", gap: 4, fontFamily: "Outfit", fontWeight: 600, fontSize: "0.8rem" }}
          aria-label={t("chat.send")}
        >
          <PaperPlaneRight size={14} weight="bold" />
        </button>
      </form>
      {error && <p data-testid={`chat-${trackingCode}-error`} style={{ marginTop: 6, fontSize: "0.75rem", color: "var(--error)" }}>{error}</p>}
    </div>
  );
}
